import { config } from '../config/environment';
import { logger } from './logger';

// Prefer environment variable for the Discovery Service
// Example: REACT_APP_DISCOVERY_API_BASE=https://<your-discovery-service>.azurewebsites.net/api
const DISCOVERY_API_BASE =
  config.discoveryApiBase ||
  ''; // Leave empty to force passing apiBase explicitly

// Optional Azure Functions host key for auth (avoid 401)
// Configure in your frontend env as REACT_APP_DISCOVERY_API_KEY
const DISCOVERY_FUNCTIONS_KEY = process.env.REACT_APP_DISCOVERY_API_KEY || '';

/**
 * Get access token for Azure AD authentication
 * @returns {Promise<string>} Access token
 */
const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/discovery-service-prod.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/discovery-service-stage.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/discovery-service-prod.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/discovery-service-stage.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with canvass-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

/**
 * Generate strategy outline for a canvass.
 * Backend route (Azure Function):
 *   POST {base}/strategy/outline/{canvassId}
 * See: Functions_Backend/discovery_service/src/functions/discoveryChat.ts (strategy-outline)
 *
 * @param {Object} options
 * @param {string} options.canvassId - Required canvassId
 * @param {string} [options.apiBase] - Optional override for base URL
 * @param {string} [options.functionsKey] - Optional Azure Functions key; defaults to REACT_APP_DISCOVERY_API_KEY
 * @returns {Promise<Object>} Strategy outline object
 */
export const generateStrategyOutline = async (options = {}) => {
  const { canvassId, apiBase, functionsKey } = options;

  if (!canvassId) throw new Error('generateStrategyOutline: canvassId is required');

  const base = apiBase || DISCOVERY_API_BASE;
  if (!base) {
    throw new Error(
      'Discovery API base URL not set. Provide REACT_APP_DISCOVERY_API_BASE or pass options.apiBase.'
    );
  }

  // Function route name is 'strategy-outline' with route 'strategy/outline/{canvassId}'
  const apiUrl = `${base}/strategy/outline/${encodeURIComponent(canvassId)}`;

  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Azure Functions key if available
    const key = functionsKey || DISCOVERY_FUNCTIONS_KEY;
    if (key) {
      headers['x-functions-key'] = key;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
    });

    const result = await response.json();

    if (!response.ok) {
      const errorMessage = result.error || `HTTP ${response.status}: ${response.statusText}`;
      logger.error('Strategy outline generation failed:', errorMessage, { canvassId, status: response.status });
      throw new Error(errorMessage);
    }

    logger.info('Strategy outline generated successfully', { canvassId });
    return result;

  } catch (error) {
    logger.error('Error generating strategy outline:', error, { canvassId });
    throw error;
  }
};

/**
 * Send chat message to discovery service for onboarding interview.
 * Backend route (Azure Function):
 *   POST {base}/chat
 * See: Functions_Backend/discovery_service/src/functions/discoveryChat.ts (discoveryChatHandler)
 *
 * @param {Object} options
 * @param {string} options.user_message - Required user message
 * @param {string} [options.conversationId] - Optional conversation ID
 * @param {Object} [options.canvassInfo] - Optional canvass information
 * @param {string[]} [options.messages] - Optional messages array with roles 'user', 'assistant', or 'intro'
 * @param {boolean} [options.endInterview] - Optional flag to end the interview
 * @param {string} [options.apiBase] - Optional override for base URL
 * @param {string} [options.functionsKey] - Optional Azure Functions key; defaults to REACT_APP_DISCOVERY_API_KEY
 * @returns {Promise<Object>} Discovery service response
 */
export const sendDiscoveryChat = async (options = {}) => {
  const { user_message, conversationId, canvassInfo, messages, endInterview, apiBase, functionsKey } = options;

  if (!user_message) throw new Error('sendDiscoveryChat: user_message is required');

  const base = apiBase || DISCOVERY_API_BASE;
  if (!base) {
    throw new Error(
      'Discovery API base URL not set. Provide REACT_APP_DISCOVERY_API_BASE or pass options.apiBase.'
    );
  }

  const apiUrl = `${base}/chat`;

  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Azure AD token for authentication
    try {
      const token = await getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (authError) {
      logger.warn('Failed to get Azure AD token, proceeding without authentication:', authError);
      // In development, this might be acceptable if SKIP_AZURE_AUTH is enabled
    }

    // Add Azure Functions key if available
    const key = functionsKey || DISCOVERY_FUNCTIONS_KEY;
    if (key) {
      headers['x-functions-key'] = key;
    }

    const requestBody = {
      user_message,
      ...(canvassInfo && { canvassInfo }),
    };

    // Only include conversationId if it's provided
    if (conversationId) {
      requestBody.conversationId = conversationId;
    }

    // Include messages for stateless frontend pattern
    if (messages && messages.length > 0) {
      requestBody.messages = messages;
    }

    // End the interview if explicitly requested
    if (endInterview) {
      requestBody.end_interview = true;
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (!response.ok) {
      const errorMessage = result.error || `HTTP ${response.status}: ${response.statusText}`;
      logger.error('Discovery chat failed:', errorMessage, { user_message, status: response.status });
      throw new Error(errorMessage);
    }

    logger.info('Discovery chat successful', { user_message, conversationId: result.conversationId });
    return result;

  } catch (error) {
    logger.error('Error sending discovery chat:', error, { user_message });
    throw error;
  }
};